import { Button, Empty, Tag } from 'antd';
import dayjs from 'dayjs';
import useEmployeeNoticesT from '@/components/email-templates/useEmployeeNoticesT';
import { DATE_FORMATS } from '@/utils/formUtils';
import { answersLine, itemLabel, itemLine } from './equipmentLines';

const day = (date) =>
  date ? dayjs(date).format(DATE_FORMATS.DISPLAY_DOTS) : '';

const APPROVAL_COLORS = {
  pending: 'gold',
  approved: 'green',
  rejected: 'red',
};
const STATE_COLORS = {
  requested: 'default',
  assigned: 'blue',
  returned: 'green',
};

/**
 * "Equipment" tile on the employee's own profile: "Zatraži opremu" (the
 * company's catalog; the request goes to the head of the sector for
 * approval first), what the employee holds now, and "Moji zahtevi" — each
 * request with its approval (waiting / approved / rejected with the reason)
 * and, once approved, every item's hand-over state. Read-only otherwise.
 */
export default function OwnEquipmentPanel({ equipment, onRequest }) {
  const { tn } = useEmployeeNoticesT();
  const requests = equipment?.requests || [];
  const assigned = equipment?.assigned || [];

  return (
    <div className="grid gap-3">
      {equipment?.enabled && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            className="btn-default btn-default--hover !rounded-[50px]"
            onClick={onRequest}
          >
            {tn('requestEquipment')}
          </Button>
          <span className="text-[12px] text-zinc-500">
            {tn('ownEquipmentHint')}
          </span>
        </div>
      )}

      {assigned.length > 0 && (
        <section className="grid gap-1.5 rounded-xl border border-blue-200 bg-blue-50/40 p-3 text-[13px]">
          <h3 className="m-0 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-500">
            {tn('currentlyAssigned')}
          </h3>
          <ul className="m-0 pl-4">
            {assigned.map((item) => (
              <li key={`${item.requestId}-${itemLabel(item)}`}>
                <span className="font-semibold text-zinc-900">
                  {itemLine(item)}
                </span>
                <span className="block text-[12px] text-zinc-500">
                  {`${tn('assignedOn')} ${day(item.assignedAt)}`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <h3 className="m-0 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-500">
        {tn('myEquipmentRequests')}
      </h3>
      {requests.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={tn('noEquipmentRequests')}
        />
      ) : (
        requests.map((request) => {
          const status = request.approval?.status || 'approved';
          const counts = status === 'approved';
          return (
            <section
              key={request._id}
              className="grid gap-1.5 rounded-xl border border-zinc-200 bg-white p-3 text-[13px]"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-bold text-zinc-900">
                  {dayjs(request.requestedAt).format(DATE_FORMATS.DISPLAY)}
                </span>
                <Tag color={APPROVAL_COLORS[status]} className="!mr-0">
                  {tn(`equipmentApprovalStates.${status}`)}
                </Tag>
              </div>
              {!request.selfRequested && request.requestedByName && (
                <span className="text-[12px] text-zinc-500">
                  {tn('requestedBy', { name: request.requestedByName })}
                </span>
              )}
              <ul className="m-0 grid list-none gap-1 p-0">
                {[...request.items, ...request.extras].map((item) => (
                  <li key={itemLabel(item)}>
                    {counts && (
                      <Tag
                        color={STATE_COLORS[item.state || 'requested']}
                        className="!mr-1.5"
                      >
                        {tn(`equipmentStates.${item.state || 'requested'}`)}
                      </Tag>
                    )}
                    {itemLine(item)}
                  </li>
                ))}
              </ul>
              {request.answers?.length > 0 && (
                <p className="m-0 text-zinc-600">{answersLine(request.answers, tn)}</p>
              )}
              {request.note && (
                <p className="m-0 whitespace-pre-line text-zinc-600">
                  <span className="font-bold text-zinc-700">{tn('note')}:</span>{' '}
                  {request.note}
                </p>
              )}
              {request.approval?.decidedAt &&
                request.approval.level !== 'self' && (
                  <span className="text-[12px] text-zinc-500">
                    {tn(
                      status === 'rejected'
                        ? 'equipmentRejectedBy'
                        : 'equipmentApprovedBy',
                      {
                        name: request.approval.decidedByName || '—',
                        date: day(request.approval.decidedAt),
                      }
                    )}
                  </span>
                )}
              {status === 'rejected' && request.approval?.note && (
                <p className="m-0 whitespace-pre-line rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-[12.5px] text-red-700">
                  <span className="font-bold">
                    {tn('equipmentRejectReason')}:
                  </span>{' '}
                  {request.approval.note}
                </p>
              )}
            </section>
          );
        })
      )}
    </div>
  );
}
